import React from "react";

interface SlideContent {
  title?: string;
  subtitle?: string;
  content?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const Entrance3Slide = {
  id: "entrance3-slide",
  name: "Entrance Slide (Particles)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
    },
    {
      id: "content",
      label: "Intro Note",
      type: "text",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "",
      subtitle = "",
      content: introNote = "",
      bgColor,
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-white relative overflow-hidden bg-gradient-to-br from-teal-600 via-teal-700 to-teal-900"
        style={{ background: bgColor }}
      >
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-teal-400/10 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-teal-900/20 to-transparent" />
          {/* Particles */}
          <div className="particles">
            {Array.from({ length: 50 }).map((_, i) => (
              <div
                key={i}
                className="particle"
                style={
                  {
                    "--x": `${Math.random() * 100}%`,
                    "--y": `${Math.random() * 100}%`,
                    "--duration": `${Math.random() * 20 + 10}s`,
                    "--delay": `${Math.random() * 5}s`,
                    "--alpha": Math.random() * 0.6 + 0.2,
                    "--size": `${Math.random() * 2 + 1}px`,
                  } as React.CSSProperties
                }
              />
            ))}
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 max-w-4xl mx-auto p-8 text-center">
          {/* Main Title */}
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("title")(e.currentTarget.textContent || "")
            }
            className="text-7xl font-bold mb-6 text-white animate-fade-in-up cursor-text"
          >
            {title || "Enter title..."}
          </div>

          {/* Subtitle */}
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("subtitle")(e.currentTarget.textContent || "")
            }
            className="text-3xl font-light mb-12 text-teal-100 animate-fade-in-up animation-delay-200 cursor-text"
          >
            {subtitle || "Enter subtitle..."}
          </div>

          {/* Intro Note */}
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("content")(e.currentTarget.textContent || "")
            }
            className="text-xl text-teal-50 max-w-2xl mx-auto animate-fade-in-up animation-delay-400 cursor-text"
          >
            {introNote || "Enter intro note..."}
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            className="w-full h-48"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              fill="rgba(255,255,255,0.1)"
            />
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              fill="rgba(255,255,255,0.05)"
            />
          </svg>
        </div>
      </div>
    );
  },
};

// Add required styles
const style = document.createElement("style");
style.textContent = `
  .particles {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .particle {
    position: absolute;
    left: var(--x);
    top: var(--y);
    width: var(--size);
    height: var(--size);
    background: white;
    border-radius: 50%;
    opacity: var(--alpha);
    animation: float var(--duration) infinite;
    animation-delay: var(--delay);
  }

  @keyframes float {
    0% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(20px, -20px);
    }
    100% {
      transform: translate(0, 0);
    }
  }
`;
document.head.appendChild(style);

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default Entrance3Slide;
